<template>
    <MonitorLayoutTemplate
        :table_title="table_title"
        :is_show_chart_dialog="is_show_chart_dialog"
        :default_value="default_value"
        :in_overview_page="in_overview_page"
    >
        <template #datetime>
            <QpeDatetime
                :props_date="date"
                :props_time="time"
                :name="default_value"
                @update-time="update_time"
                @change-parent-date-time="change_parent_date_time"
            />
        </template>

        <template #datetime_interval>
            <div style="display: flex; align-items: center;">
                <QpeDatetimeHistory 
                    @set_history_date_list="set_history_date_list"
                    @open_hint_dialog="open_hint_dialog"
                />
            </div>
        </template>

        <template #description>
            <SignsCalculator 
                :data="sign_data"
            />
            <MapDisplaySwitch 
                v-if="table_data.length"
                :table_id="default_value"
                :table_data="immutable_table_data"
            />
        </template>
        <template #description_history>
            <SignsCalculator 
                :data="[]"
            />
        </template>

        <template #signs>
            <MonitorSigns v-if="sign_data"
                :data="sign_data"
            />
        </template>

        <template #data_table="slot_props">
            <QpeDatatable
                :data="table_data"
                :headers="header"
                :name="default_value"
                :error_text="error_text"
                :search="slot_props.search"
                :should_draw_diagram_header="should_draw_diagram_header"
                :enable_diagram_slot="true"
                :should_display_info_header_key_value="should_display_info_header_key_value"
                :generate_tooltop_display_info="generate_tooltop_display_info_for_table"
                :should_show_raw_html_header="should_show_raw_html_header"
                :show_download_doc_btn="show_download_doc_btn"
                :show_download_pdf_btn="show_download_pdf_btn"
                @update-time="update_time"
                @change-parent-date-time="change_parent_date_time"
                @open-chart="assign_chart_component"
                :is_show_chart_dialog_parent.sync="is_show_chart_dialog"
            >
                <template #diagram_dialog="{ close_dialog }">
                    <GisChart v-if="is_show_chart_dialog"
                        id="belongs_monitor_chart"
                        :chart_options="chart_options"
                        :default_variable="$t('__rainfall.rainfall')"
                        :prop_rain_selected="current_table_header"
                        @close-chart-dialog="close_dialog"
                    />
                </template>
                <template v-if="show_customize_explanation" 
                    #table_description
                >
                    <slot name="explanation_remarks" />
                </template>
            </QpeDatatable>
        </template>
        <template #history_data_table="slot_props">
            <QpeDatatableHistory 
                :name="default_value"
                :search="slot_props.search"
                :search_column="slot_props.search_column"
                :default_value="default_value"
                :history_date_list="history_date_list"
                :show_download_pdf_btn="show_download_pdf_btn"
                @set_history_sign_data="set_history_sign_data"
            />
        </template>

        <template #hint_dialog>
            <QpeDialogHistory 
                :is_open_dialog="is_open_dialog"
                @close_hint_dialog="close_hint_dialog"
            />
        </template>
    </MonitorLayoutTemplate>
</template>

<script>
    import { redirect_monitor_table_ajax } from "@/utils/monitor/monitor.js"
    import { get_stations_history_params } from "@/utils/chart/chart_ajax.js"
    import { judge_invalid_data_by_keys, sort_table_data, reconstruct_alert_content } from "@/utils/monitor/common_tool"
    import { monitor_mixin } from "@/utils/monitor/monitor_mixin.js"
    import {
        TABLE_VALUE_TEXT_MAPPING,
        monitor_signs_normal_mapping, 
    } from "@/config/monitor_config.js"
    import { mapState } from "vuex"
    import MonitorLayoutTemplate from "@/components/monitor/_tool/layout_template.vue"
    import QpeDatetime from "@/components/monitor/qpe_datetime.vue"
    import SignsCalculator from "@/components/monitor/_tool/signs_calculator.vue";
    import MapDisplaySwitch from "@/components/monitor/_tool/map_display_switch.vue"
    import MonitorSigns from "@/components/monitor/_tool/monitor_signs.vue"
    import QpeDatatable from "@/components/monitor/qpe_datatable.vue"    
    import QpeDatatableHistory from "@/components/monitor/qpe_datatable_history.vue"
    import QpeDatetimeHistory from "@/components/monitor/qpe_datetime_history.vue"
    import QpeDialogHistory from "@/components/monitor/qpe_dialog_history.vue"

    export default {
        name: 'ChildNormalAlert',
        
        components: { 
            MonitorLayoutTemplate,
            QpeDatetime,
            SignsCalculator,
            MapDisplaySwitch,
            MonitorSigns, 
            QpeDatatable, 
            QpeDatatableHistory,
            QpeDatetimeHistory,
            QpeDialogHistory,
            GisChart: () => import('@/components/chart/gis_chart.vue')
        },

        mixins: [monitor_mixin],

        props: {
            default_value: {
                type: Number,
                required: true,
            },
            in_overview_page: {
                type: Boolean,
                default() {
                    return false;
                }
            },
            show_download_doc_btn: {
                type: Boolean,
                default: false
            },
            show_customize_explanation: {
                type: Boolean,
                default() {
                    return false
                }
            }
        },

        data() {
            return {
                // 排序使用
                table_data: [],
                header: [],
                error_text: '',

                immutable_table_data: [],

                should_draw_diagram_header: ['10分鐘', '1小時', '3小時', '6小時', '12小時', '24小時'],

                is_show_chart_dialog: false,
                chart_options: {
                    type: { 'gauge': true }
                },
                current_table_header: '',

                should_display_info_header_key_value: {
                    '警示理由': {},
                },

                should_show_raw_html_header: ['警示'],

                history_date_list: [],
                is_open_dialog: false,
                // history_sign_data: [], // 如果有要算sign個數之後再加
            }
        },
        
        computed: {
            ...mapState([
                'client_name',
            ]),

            table_title() {
                return `${TABLE_VALUE_TEXT_MAPPING[this.default_value]['tag_name']}資訊`
            },
        
            sign_data() {
                return this.immutable_table_data.map((item) => {
                    const each_sign_data = {
                        idx: item['項次'] || item['編號'],
                        signs_num: monitor_signs_normal_mapping[item['警示']] || '-'
                    }
                    return each_sign_data
                });
            },

            enable_filter_unalert_data() {
                return this.in_overview_page
            },

            show_download_pdf_btn() {
                return !this.in_overview_page
            },
        },

        created() {
            redirect_monitor_table_ajax.call(this, this.default_value)
        },

        methods: {
            generate_table_data(data, header) {
                this.immutable_table_data = data;
                this.header = header;
                
                let table_data = data
                if(this.enable_filter_unalert_data) {
                    table_data = data.filter(datum => judge_invalid_data_by_keys(datum, ['警示']));
                }

                const sorted_data = sort_table_data(table_data, monitor_signs_normal_mapping)
                this.table_data = reconstruct_alert_content(sorted_data, this.should_show_raw_html_header);
            },

            generate_tooltop_display_info_for_table(row) {
                return row['警示條件']
            },

            async assign_chart_component(header_name, choosed_value, current_row_data) {
                this.current_table_header = header_name
                this.chart_options = { ...this.chart_options, ...get_stations_history_params.call(this, current_row_data) }
            },

            set_history_date_list(list) {
                this.history_date_list = list;
            },

            open_hint_dialog() {
                this.is_open_dialog = true;
            },

            close_hint_dialog() {
                this.is_open_dialog = false;
            },

            set_history_sign_data(data) {
                // 如果有要算sign個數之後再加
                // this.history_sign_data = data;
            },
        },
    }
</script>